/**
 *
 * FormController
 *
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';

import Summary from 'pages/SummaryPage/loadable';
import Transactions from 'containers/Transactions/Loadable';
import Settlements from 'containers/Settlements/Loadable';
import Timeline from 'containers/Timeline/Loadable';
import SettlementsDetailsPage from 'containers/SettlementDetails/Loadable';
import SarConfirmModal from 'components/SarConfirmModal/Loadable';
import ForgotPassword from 'containers/ForgotPassword/Loadable';
import ResetPassword from 'containers/ResetPassword/Loadable';
import APIKeys from 'containers/ApiKeys/Loadable';
import SettlementBankDetails from 'containers/SettlementBankDetails/Loadable';
import EasySplit from 'containers/EasySplit/Loadable';
import EasySplitDetails from 'containers/EasySplitDetails/Loadable';
import PaymentMethods from 'containers/PaymentMethods/Loadable';
import Signup from 'containers/Signup/Loadable';
import Login from 'containers/Login/Loadable';
import VerifyPasswordModal from 'containers/VerifyPasswordModal/Loadable';
import RiskShield from 'containers/Riskshield';
import VerifyEmail from 'containers/VerifyEmail/Loadable';
import PaymentLinks from 'containers/PaymentLinks/Loadable';
import PaymentForms from 'containers/PaymentForms/Loadable';
import PaymentButtons from 'containers/PaymentButtons/Loadable';
import Customers from 'containers/Customers/Loadable';
import ProductLinks from 'containers/ProductLinks/Loadable';
import Reports from 'containers/Reports/Loadable';
import Settings from 'containers/Settings/Loadable';
import EmailNotifications from 'containers/EmailNotifications/Loadable';
import UserNotAuthorized from 'components/UserNotAuthorizedModal';
import VerifyOTPModal from 'containers/VerifyOTPModal/Loadable';
import Developers from 'containers/Developers/Loadable';
import ProductDescription from 'containers/ProductDescription/Loadable';
import PaymentLinkDescription from 'containers/PaymentLinkDescription/Loadable';
import EPOS from 'containers/EPOS/Loadable';
import Webhooks from 'containers/Webhooks/Loadable';
import Subscriptions from 'containers/Subscriptions';
import OrdersDescription from 'containers/OrdersDescription/Loadable';
import Router from 'containers/Router/Loadable';
import OffersPage from 'pages/OffersPage/loadable';
import OffersDescription from 'containers/OffersDescription/Loadable';
import PaymentApps from 'containers/PaymentApps/Loadable';
import RouterAnalytics from 'containers/RouterAnalytics/Loadable';
import QrCode from 'containers/QrCode/Loadable';
import DisputeDetails from 'containers/DisputeDetails/Loadable';
import WalletDetailsPage from 'containers/WalletDetailsPage/Loadable';
import Incidents from 'containers/Incidents/Loadable';
import CollectionPointDetails from 'containers/CollectionPointDetails/Loadable';
import SettlementReconDetails from 'containers/SettlementReconDetails/Loadable';
import AnalyticsV2 from 'containers/AnalyticsV2/Loadable';
import BrandingV2 from 'containers/BrandingV2/Loadable';
import VBAConfiguration from 'containers/VBAConfiguration/lodable';
import Ecom360 from 'containers/Ecom360/Loadable';
import NonShopifyEcom360 from 'containers/NonShopifyEcom360/Loadable';
import NonShopifyEcom360Activated from 'containers/NonShopifyEcom360Activated/loadable';
import EmbeddableComponents from 'containers/EmbeddableComponents/Loadable';
import CheckoutOne from 'containers/CheckoutOneCustomisation/Loadable';
import RtoBlackListDetails from 'containers/RtoBlackListDetails/Loadable';
import RtoReduction from 'containers/RtoReduction/Loadable';
import RefundsDetail from 'containers/RefundsDetail/Loadable';
import RecoveryCampaign from 'containers/RecoveryCampaign/loadable';
import RecoveryCampaignConfiguration from 'containers/RecoveryCampaignConfiguration/loadable';
import Playground from 'containers/Playground/Loadable';
import PaymentVerification from 'containers/PaymentVerification/Loadable';
import PaymentVerificationDetails from 'containers/PaymentVerificationDetails/Loadable';
import OffersCreateV2 from 'containers/OffersCreateV2/Loadable';
import Workflows from 'containers/Workflows/Loadable';

// Utils
import { COD, RECOVERY_CAMPAIGN } from 'utils/constants';

export function FormController({ componentName, ...props }) {
  const componentMapper = {
    SIGNUP: Signup,
    LOGIN: Login,
    FORGOT_PASSWORD: ForgotPassword,
    RESET_PASSWORD: ResetPassword,

    TRANSACTIONS: Transactions,
    SUMMARY: Summary,
    TIMELINE: Timeline,
    VERIFY_EMAIL: VerifyEmail,
    PAYMENT_LINKS: PaymentLinks,
    PAYMENT_FORMS: PaymentForms,
    PAYMENT_BUTTONS: PaymentButtons,
    CUSTOMERS: Customers,
    EASY_SPLIT: EasySplit,
    SETTLEMENTS: Settlements,
    SETTLEMENT_DETAILS: SettlementsDetailsPage,
    SETTLEMENT_RECON_DETAILS: SettlementReconDetails,
    PRODUCT_LINKS: ProductLinks,
    REPORTS: Reports,
    SETTINGS: Settings,
    BRANDING: BrandingV2,
    PAYMENT_METHODS: PaymentMethods,
    SETTLEMENT_BANK_DETAILS: SettlementBankDetails,
    NOTIFICATIONS: EmailNotifications,
    API_KEYS: APIKeys,
    VERIFY_PASSWORD: VerifyPasswordModal,
    VERIFY_OTP: VerifyOTPModal,
    VERIFY_G2FA: VerifyOTPModal,
    USER_NOT_AUTHORIZED: UserNotAuthorized,
    DEVELOPERS: Developers,
    SOFTPOS: EPOS,
    PRODUCT_DESCRIPTION: ProductDescription,
    PAYMENT_LINK_DESCRIPTION: PaymentLinkDescription,
    WEBHOOKS: Webhooks,
    SUBSCRIPTIONS: Subscriptions,
    ORDERS_DESCRIPTION: OrdersDescription,
    ROUTER: Router,
    OFFERS: OffersPage,
    OFFERS_DESCRIPTION: OffersDescription,
    APPS: PaymentApps,
    ROUTER_ANALYTICS: RouterAnalytics,
    ANALYTICS_V2: AnalyticsV2,
    QR_CODE: QrCode,
    SAR_PENDING: SarConfirmModal,
    EASY_SPLIT_DETAILS: EasySplitDetails,
    DISPUTE_DETAILS: DisputeDetails,
    WALLET_DETAILS: WalletDetailsPage,
    STATUS_PAGE: Incidents,
    COLLECTION_POINT_DETAILS: CollectionPointDetails,
    RISKSHIELD: RiskShield,
    VBA_CONFIGURATION: VBAConfiguration,
    ECOM_360: Ecom360,
    ECOM_360_V2: NonShopifyEcom360,
    ECOM_360_ACTIVATED: NonShopifyEcom360Activated,
    CHECKOUT_ONE: CheckoutOne,
    EMBEDDABLE_COMPONENTS: EmbeddableComponents,
    [COD]: RtoReduction,
    BLACK_LIST_DETAILS: RtoBlackListDetails,
    RECOVERY_CAMPAIGN_CONFIGURATION: RecoveryCampaignConfiguration,
    REFUNDS_DETAIL: RefundsDetail,
    SR_PLAYGROUND: Playground,
    PAYMENT_VERIFICATION: PaymentVerification,
    PAYMENT_VERIFICATION_DETAILS: PaymentVerificationDetails,
    [RECOVERY_CAMPAIGN]: RecoveryCampaign,
    OFFERS_V2_CREATE: OffersCreateV2,
    WORKFLOWS: Workflows,
  };

  const TagName = componentMapper[componentName];
  return <TagName {...props} />;
}

FormController.propTypes = {
  componentName: PropTypes.any,
  header: PropTypes.any,
  subheaders: PropTypes.any,
  className: PropTypes.any,
};

export default memo(FormController);
